// Format & quality detection helpers for the Local Library redesign.
//
// Built around two facts about the existing data:
//   - DSD files (DSF / DFF) often carry sample_rate values like 2822400 /
//     5644800 / 11289600 / 22579200 and a bit_depth of 1 — useless to show
//     to a user. We fold those into DSD64 / DSD128 / DSD256 / DSD512.
//   - PCM files store sample_rate in Hz and bit_depth in bits. Hi-res =
//     bit_depth >= 24 OR sample_rate >= 88200 (matches qualifiesHiResAudioBadge).
//
// All exports are pure — safe to call from render and from filter predicates.
//
// UNIFIED FILTER MODULE: Health cards, sidebar presets, and filter chips
// all call the same exported functions so counts and filtered lists always agree.

const DSD_RATE_TO_LABEL = new Map([
  [2822400, 'DSD64'],
  [5644800, 'DSD128'],
  [11289600, 'DSD256'],
  [22579200, 'DSD512'],
]);

const DSD_LABEL_TO_RATE = new Map([
  ['DSD64', 2822400],
  ['DSD128', 5644800],
  ['DSD256', 11289600],
  ['DSD512', 22579200],
]);

const DSD_RATES = new Set([2822400, 5644800, 11289600, 22579200]);
const DSD_FORMATS = new Set(['DSF', 'DFF', 'DSD', 'DSDIFF']);
const LOSSLESS_FORMATS = new Set(['FLAC', 'ALAC', 'WAV', 'AIFF', 'AIF', 'DSF', 'DFF', 'DSD', 'DSDIFF']);

function asString(v) {
  return v == null ? '' : String(v);
}

function asNum(v) {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** True for DSF/DFF/DSD/DSDIFF by codec, file extension, OR by an obvious DSD sample rate. */
export function isDsd(album) {
  if (!album) return false;
  const fmt = asString(album.format || album.primary_format).toUpperCase();
  if (DSD_FORMATS.has(fmt)) return true;
  const ext = asString(album.file_extension || album.extension).toLowerCase().replace(/^\./, '');
  if (ext === 'dsf' || ext === 'dff') return true;
  const sr = asNum(album.sample_rate);
  if (sr && DSD_RATES.has(sr)) return true;
  return false;
}

export function isDsd64(album) {
  if (!isDsd(album)) return false;
  const sr = asNum(album.sample_rate);
  return sr === 2822400;
}
export function isDsd128(album) {
  if (!isDsd(album)) return false;
  const sr = asNum(album.sample_rate);
  return sr === 5644800;
}
export function isDsd256(album) {
  if (!isDsd(album)) return false;
  const sr = asNum(album.sample_rate);
  return sr === 11289600;
}
export function isDsd512(album) {
  if (!isDsd(album)) return false;
  const sr = asNum(album.sample_rate);
  return sr === 22579200;
}

/** Returns 'DSD64' | 'DSD128' | 'DSD256' | 'DSD512' for DSD content, else ''. */
export function getDsdLabel(album) {
  if (!isDsd(album)) return '';
  const sr = asNum(album?.sample_rate);
  if (sr && DSD_RATE_TO_LABEL.has(sr)) return DSD_RATE_TO_LABEL.get(sr);
  const dsdRate = asNum(album?.dsd_rate);
  if (dsdRate) return `DSD${dsdRate}`;
  return 'DSD';
}

/** Hi-res by audiophile convention: bit depth >= 24 OR sample rate >= 88.2 kHz OR DSD. */
export function isHiRes(album) {
  if (!album) return false;
  if (isDsd(album)) return true;
  const bd = asNum(album.bit_depth);
  const sr = asNum(album.sample_rate);
  if (bd && bd >= 24) return true;
  if (sr && sr >= 88200) return true;
  return false;
}

/** Lossless by codec family — used by the muted "Lossless" pill on CD-quality content. */
export function isLossless(album) {
  if (!album) return false;
  if (album.is_lossless === true) return true;
  const fmt = asString(album.format || album.primary_format).toUpperCase();
  return LOSSLESS_FORMATS.has(fmt);
}

/**
 * Compact human badge for an album cover.
 *   DSD content       → "DSD64" | "DSD128" | …
 *   Hi-res PCM        → "FLAC 24/192", "ALAC 24/96", …
 *   Lossless CD       → "FLAC", "ALAC", "WAV"
 *   Lossy             → "MP3", "AAC", …
 *   Unknown           → '' (caller should hide the badge)
 */
export function getFormatBadge(album) {
  if (!album) return '';
  if (isDsd(album)) return getDsdLabel(album) || 'DSD';

  const fmt = asString(album.format || album.primary_format).toUpperCase();
  const bd = asNum(album.bit_depth);
  const sr = asNum(album.sample_rate);

  if (isHiRes(album) && fmt && bd && sr) {
    const khz = Math.round(sr / 1000);
    return `${fmt} ${bd}/${khz}`.trim();
  }
  if (fmt) return fmt;
  if (bd && sr) return `${bd}/${Math.round(sr / 1000)}`;
  return '';
}

/**
 * "Local" / "NAS" / "Qobuz" pill for either an album OR a queue item. We
 * read several optional fields because the shape varies across pages.
 */
export function getSourceBadge(item) {
  if (!item) return '';

  // Streaming services first — most authoritative.
  const svc = asString(item.service || item.source_service || item.streaming_service).toLowerCase();
  if (svc === 'qobuz') return 'Qobuz';
  if (svc === 'tidal') return 'Tidal';
  if (svc === 'spotify') return 'Spotify';
  if (svc === 'presto' || svc === 'prestomusic') return 'Presto';

  const sourceLabel = asString(item.source).toLowerCase();
  if (sourceLabel.startsWith('qobuz')) return 'Qobuz';
  if (sourceLabel.startsWith('tidal')) return 'Tidal';

  // __source / isLocalLibrary mirror the existing AlbumGrid convention.
  if (item.__source === 'local_library' || item.isLocalLibrary === true) return 'Local';

  // Network share heuristic — paths starting with /Volumes/<share>/ on macOS
  // or //host/share on UNC are NAS unless the share is the boot drive.
  const path = asString(item.path || item.file_path || item.source_path);
  if (path) {
    if (/^\/\//.test(path) || /^\\\\/.test(path)) return 'NAS';
    if (/^\/Volumes\/(?!Macintosh HD)/i.test(path)) return 'NAS';
  }

  // Fallback: if it has a local source_id it's local.
  if (item.source_id || item.album_key) return 'Local';

  return '';
}

/** 64-char blake3/sha256 hex — local library embedded or folder cover hash. */
function isCoverHash(value) {
  const h = asString(value);
  return /^[0-9a-f]{64}$/i.test(h);
}

function hasLocalCoverArt(album) {
  if (!album) return false;
  if (isCoverHash(album.embedded_cover_hash)) return true;
  if (isCoverHash(album.folder_cover_hash)) return true;
  return false;
}

function isBadCoverUrl(url) {
  if (!url) return true;
  if (url.includes('/front-500')) return true;
  if (url.includes('coverartarchive.org/release-group/')) return true;
  return false;
}

/**
 * True when the album has no displayable cover art (local hash, valid URL, or
 * user-selected online art). Used by filter chips, health cards, and presets.
 */
export function isMissingArtwork(album) {
  if (!album) return false;
  if (album.user_override_source === 'none') return true;
  if (hasLocalCoverArt(album)) return false;
  const url = asString(album.cover_art_url || album.coverUrl || album.cover_url);
  if (!url) return true;
  return isBadCoverUrl(url);
}

/** Sort keys for album wall and maintenance lists. */
export const ALBUM_SORT_OPTIONS = [
  { value: 'title_asc', label: 'Title A–Z' },
  { value: 'title_desc', label: 'Title Z–A' },
  { value: 'artist_asc', label: 'Artist A–Z' },
  { value: 'artist_desc', label: 'Artist Z–A' },
  { value: 'added_desc', label: 'Date added (newest)' },
  { value: 'added_asc', label: 'Date added (oldest)' },
];

export function sortAlbums(albums, sortKey = 'title_asc') {
  const list = Array.isArray(albums) ? [...albums] : [];
  const titleOf = (a) => asString(a?.title || a?.album).toLowerCase();
  const artistOf = (a) => asString(a?.artist_name || a?.artist).toLowerCase();
  const dateOf = (a) => asString(a?.created_at || a?.updated_at || a?.scanned_at);
  switch (sortKey) {
    case 'title_desc':
      return list.sort((a, b) => titleOf(b).localeCompare(titleOf(a)));
    case 'artist_asc':
      return list.sort((a, b) => artistOf(a).localeCompare(artistOf(b)) || titleOf(a).localeCompare(titleOf(b)));
    case 'artist_desc':
      return list.sort((a, b) => artistOf(b).localeCompare(artistOf(a)) || titleOf(a).localeCompare(titleOf(b)));
    case 'added_desc':
      return list.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
    case 'added_asc':
      return list.sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
    case 'title_asc':
    default:
      return list.sort((a, b) => titleOf(a).localeCompare(titleOf(b)));
  }
}

// ───────────────────────────────────────────────────────────────────────
// Unified matching helpers used by chips, sidebar presets, AND health cards.
// Every filter path MUST call these so counts and filtered lists agree.
// ───────────────────────────────────────────────────────────────────────

/** Match an album against a sample-rate filter value (handles "DSD64" etc). */
export function matchesSampleRate(album, filterValue) {
  if (!filterValue) return true;
  if (DSD_LABEL_TO_RATE.has(filterValue)) {
    if (!isDsd(album)) return false;
    const wantRate = DSD_LABEL_TO_RATE.get(filterValue);
    const sr = asNum(album.sample_rate);
    return sr === wantRate;
  }
  return asNum(album.sample_rate) === Number(filterValue);
}

/** Infer container/codec from album fields when SQL format column is empty. */
export function inferAlbumFormat(album) {
  if (!album) return '';
  const direct = asString(album.format || album.primary_format || album.audioFormat).toUpperCase();
  if (direct) return direct;
  const path = asString(
    album.sample_file_path || album.library_folder_path || album.folder_path || '',
  );
  const m = path.match(/\.([a-z0-9]+)$/i);
  if (!m) return '';
  const ext = m[1].toUpperCase();
  if (ext === 'M4A') return 'AAC';
  return ext;
}

/** Match an album against a format filter value (handles DSD family). */
export function matchesFormat(album, filterValue) {
  if (!filterValue) return true;
  const want = filterValue.toUpperCase();
  if (want === 'DSF' || want === 'DSD') return isDsd(album);
  const fmt = inferAlbumFormat(album);
  if (fmt === want) return true;
  if (want === 'AAC' && (fmt === 'M4A' || fmt === 'AAC')) return true;
  if (want === 'M4A' && (fmt === 'M4A' || fmt === 'AAC')) return true;
  return false;
}

/** Match an album against a bit-depth filter value. */
export function matchesBitDepth(album, filterValue) {
  if (!filterValue) return true;
  return asNum(album.bit_depth) === Number(filterValue);
}

/** True if the album was added within the last N days. */
export function isRecentlyAdded(album, days = 30) {
  if (!album) return false;
  const ts = album.created_at || album.updated_at;
  if (!ts) return false;
  const cutoff = Date.now() - days * 86400 * 1000;
  return new Date(ts).getTime() >= cutoff;
}

// ───────────────────────────────────────────────────────────────────────
// Duplicate detection
// ───────────────────────────────────────────────────────────────────────

const SUFFIX_PATTERN = /\s*[\(\[](?:remastered|deluxe|deluxe edition|remaster|expanded|expanded edition|bonus track[s]?|special edition|anniversary edition|collector'?s edition|super deluxe|mono|stereo|hi-?res|mqa)[\)\]]\s*/gi;

/** Normalize title/artist for duplicate grouping — tolerant of "&" vs "and", punctuation, etc. */
function normalizeDuplicateKey(str, { artist = false } = {}) {
  if (!str) return '';
  let s = String(str).toLowerCase().trim();
  if (artist) {
    s = s.replace(/\s*&\s*/g, ' and ');
    s = s.replace(/\bfeat\.?\b|\bfeaturing\b|\bft\.?\b/g, ' ');
  }
  s = s.replace(SUFFIX_PATTERN, '');
  s = s.replace(/[^\w\s]/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

function albumIdentityKey(album) {
  if (!album) return '';
  const title = normalizeDuplicateKey(album.title || album.album || '');
  const artist = normalizeDuplicateKey(
    album.artist_name || album.album_artist || album.artist || '',
    { artist: true },
  );
  if (!title) return '';
  return `${title}|||${artist}`;
}

export function getAlbumId(album) {
  return album?.album_key || album?.id || '';
}

/**
 * Groups albums by normalized (title + artist) key.
 * Returns a Map<string, album[]> where each entry has 2+ albums.
 */
export function findDuplicates(albums) {
  if (!Array.isArray(albums)) return new Map();
  const groups = new Map();
  for (const a of albums) {
    if (!a) continue;
    const key = albumIdentityKey(a);
    if (!key || key === '|||') continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(a);
  }
  const dupes = new Map();
  for (const [key, group] of groups) {
    if (group.length >= 2) dupes.set(key, group);
  }
  return dupes;
}

/**
 * Pre-computed duplicate lookup: group map, per-album metadata, and id set.
 * Use in maintenance lists to badge rows and drive the resolve panel.
 */
export function buildDuplicateLookup(albums) {
  const groups = findDuplicates(albums);
  const duplicateIds = new Set();
  const byAlbumId = new Map();
  for (const [groupKey, copies] of groups) {
    for (const a of copies) {
      const id = getAlbumId(a);
      if (!id) continue;
      duplicateIds.add(id);
      byAlbumId.set(id, { groupKey, copies, count: copies.length });
    }
  }
  return { groups, duplicateIds, byAlbumId };
}

/**
 * Returns a Set of album IDs (album_key or id) that have at least one duplicate.
 * Pre-compute once when albums change, then pass to makeAlbumFilterPredicate.
 */
export function getDuplicateIds(albums) {
  const dupes = findDuplicates(albums);
  const ids = new Set();
  for (const group of dupes.values()) {
    for (const a of group) {
      ids.add(a.album_key || a.id);
    }
  }
  return ids;
}

/** Check if a single album is in the pre-computed duplicate set. */
export function isDuplicate(album, duplicateIds) {
  if (!album || !duplicateIds) return false;
  return duplicateIds.has(album.album_key || album.id);
}

// ───────────────────────────────────────────────────────────────────────
// Dropdown options for FilterChips
// ───────────────────────────────────────────────────────────────────────

/** Sample-rate dropdown options shared by the FilterChips. */
export const SAMPLE_RATE_OPTIONS = [
  { value: '44100', label: '44.1 kHz' },
  { value: '48000', label: '48 kHz' },
  { value: '88200', label: '88.2 kHz' },
  { value: '96000', label: '96 kHz' },
  { value: '176400', label: '176.4 kHz' },
  { value: '192000', label: '192 kHz' },
  { value: '352800', label: '352.8 kHz' },
  { value: 'DSD64', label: 'DSD64' },
  { value: 'DSD128', label: 'DSD128' },
  { value: 'DSD256', label: 'DSD256' },
  { value: 'DSD512', label: 'DSD512' },
];

/** Format dropdown options shared by the FilterChips. */
export const FORMAT_OPTIONS = [
  { value: 'FLAC', label: 'FLAC' },
  { value: 'DSF', label: 'DSD' },
  { value: 'WAV', label: 'WAV' },
  { value: 'AIFF', label: 'AIFF' },
  { value: 'M4A', label: 'AAC / ALAC' },
  { value: 'MP3', label: 'MP3' },
];

/** Bit-depth dropdown options shared by the FilterChips. */
export const BIT_DEPTH_OPTIONS = [
  { value: '16', label: '16-bit' },
  { value: '24', label: '24-bit' },
  { value: '32', label: '32-bit' },
];

// ───────────────────────────────────────────────────────────────────────
// Unified predicate factory
// ───────────────────────────────────────────────────────────────────────

/**
 * Predicate factory used by the in-memory filter. Returns `true` for albums
 * that pass every active filter.
 *
 * @param {object} filters - LibraryActiveFilters from the store
 * @param {object} [opts] - Optional context for presets that need the full list:
 *   - duplicateIds: Set<string> from getDuplicateIds(allAlbums)
 */
export function makeAlbumFilterPredicate(filters, opts = {}) {
  if (!filters) return () => true;
  const { duplicateIds } = opts;

  return (album) => {
    if (!album) return false;

    if (filters.format && !matchesFormat(album, filters.format)) return false;
    if (filters.sampleRate && !matchesSampleRate(album, filters.sampleRate)) return false;
    if (filters.bitDepth && !matchesBitDepth(album, filters.bitDepth)) return false;

    if (filters.sourceId) {
      if (asString(album.source_id) !== asString(filters.sourceId)) return false;
    }

    if (filters.artwork === 'has_artwork' && isMissingArtwork(album)) return false;
    if (filters.artwork === 'missing_artwork' && !isMissingArtwork(album)) return false;

    if (filters.recentlyAdded) {
      const days =
        filters.recentlyAdded === '1d' ? 1 :
        filters.recentlyAdded === '7d' ? 7 :
        filters.recentlyAdded === '30d' ? 30 :
        filters.recentlyAdded === '90d' ? 90 : 0;
      if (days > 0 && !isRecentlyAdded(album, days)) return false;
    }

    if (filters.preset) {
      switch (filters.preset) {
        case 'hires':
          if (!isHiRes(album)) return false;
          break;
        case 'dsd':
          if (!isDsd(album)) return false;
          break;
        case 'needs_artwork':
          if (!isMissingArtwork(album)) return false;
          break;
        case 'recently_added':
          if (!isRecentlyAdded(album, 30)) return false;
          break;
        case 'duplicates':
          if (!duplicateIds || !isDuplicate(album, duplicateIds)) return false;
          break;
        case 'needs_attention': {
          const status = album.enrichment_status;
          const needsReview = status === 'needs_review' || status === 'unidentified';
          const missingArt = isMissingArtwork(album);
          const dup = duplicateIds ? isDuplicate(album, duplicateIds) : false;
          if (!needsReview && !missingArt && !dup) return false;
          break;
        }
        case 'unmatched': {
          const es = album.enrichment_status;
          if (es !== 'unidentified' && es !== 'needs_review') return false;
          break;
        }
        // Presets without backing data show a friendly empty state.
        case 'vinyl_rips':
        case 'mono':
        case 'rare':
        case 'favorites':
          return false;
        default:
          break;
      }
    }

    return true;
  };
}
